import * as React from "react";
import {
  IOfferType,
  IOrderCancellationReason,
  IOrderPaymentStatus,
  IOrderStatus,
} from "@nju/data-access-portal-order";
import { Checkbox, Combobox } from "@nju/form";
import { Icon } from "@nju/icons";
import { Button, Text } from "@nju/ui";
import {
  mapOrderCancellationReason,
  mapOrderPaymentStatusName,
  mapOrderStatusName,
} from "../utils";

const orderStatus: Array<{ id: IOrderStatus | IOrderStatus[]; value: string }> =
  [
    {
      id: "Cancelled",
      value: mapOrderStatusName("Cancelled"),
    },
    {
      id: "Completed",
      value: mapOrderStatusName("Completed"),
    },
    {
      id: "Draft",
      value: mapOrderStatusName("Draft"),
    },
    {
      id: "FulfillmentFinishing",
      value: mapOrderStatusName("FulfillmentFinishing"),
    },
    {
      id: "InProgressFulfillment",
      value: mapOrderStatusName("InProgressFulfillment"),
    },
    {
      id: "InProgressPayment",
      value: mapOrderStatusName("InProgressPayment"),
    },
    {
      id: "InProgressDelivery",
      value: mapOrderStatusName("InProgressDelivery"),
    },
    {
      id: "PendingCancellation",
      value: mapOrderStatusName("PendingCancellation"),
    },
    {
      id: "Submitted",
      value: mapOrderStatusName("Submitted"),
    },
  ];

const cancellationReason: Array<{
  id: IOrderCancellationReason | IOrderCancellationReason[];
  value: string;
}> = [
  {
    id: "userRequest",
    value: mapOrderCancellationReason("userRequest"),
  },
  {
    id: "agentRequest",
    value: mapOrderCancellationReason("agentRequest"),
  },
  {
    id: "unsuccessfulDelivery",
    value: mapOrderCancellationReason("unsuccessfulDelivery"),
  },
  {
    id: "fulfillmentError",
    value: mapOrderCancellationReason("fulfillmentError"),
  },
  {
    id: "orderSubmissionError",
    value: mapOrderCancellationReason("orderSubmissionError"),
  },
  {
    id: "NOSS_FAILED_BMT",
    value: mapOrderCancellationReason("NOSS_FAILED_BMT"),
  },
  {
    id: "NOSS_FAILED_BMK",
    value: mapOrderCancellationReason("NOSS_FAILED_BMK"),
  },
];

const paymentStatus: Array<{
  id: IOrderPaymentStatus | IOrderPaymentStatus[];
  value: string;
}> = [
  {
    id: "COMPLETED",
    value: mapOrderPaymentStatusName("COMPLETED"),
  },
  {
    id: ["FAILED", "CANCELLED"],
    value: mapOrderPaymentStatusName("FAILED"),
  },
  {
    id: "NEW",
    value: mapOrderPaymentStatusName("NEW"),
  },
  {
    id: ["PENDING", "PENDING_REPEATING", "PENDING_REPEATING_RETRY"],
    value: mapOrderPaymentStatusName("PENDING"),
  },
];

const offerType: Array<{
  id: IOfferType;
  value: string;
}> = [
  {
    id: "Mobile",
    value: "nju subskrypcja",
  },
  {
    id: "Ftth",
    value: "nju światłowód",
  },
];

interface IProps {
  onOrderStatusChange: (value: string[]) => void;
  onPaymentStatusChange: (value: string[]) => void;
  onOfferTypeChange: (value: string[]) => void;
  onCancellationReasonChange: (value: string[]) => void;
}

export function Filters({
  onOrderStatusChange,
  onPaymentStatusChange,
  onOfferTypeChange,
  onCancellationReasonChange,
}: IProps) {
  return (
    <div className="flex flex-col w-full only-mobile:gap-3 gap-4">
      <div className="flex gap-x-2 gap-y-3 only-mobile:flex-col">
        <Combobox
          className="w-full"
          inputDisplayValue={(items) => {
            return items
              .map((item) => {
                return item.value;
              })
              .join(", ");
          }}
          itemComparator={(first, second) => {
            return first.id === second.id;
          }}
          items={offerType}
          label="usługa"
          listItemRenderer={({ item, isSelected }) => {
            return (
              <div>
                <Checkbox isSelected={isSelected} name={item.value}>
                  <strong>{item.value}</strong>
                </Checkbox>
              </div>
            );
          }}
          maxListHeight="auto"
          onSelect={(values) => {
            onOfferTypeChange(
              values.map((item) => {
                return item.id as string;
              }),
            );
          }}
          openOnFocus={true}
          prefix={({ items }) => {
            return (
              <div className="flex justify-center items-center bg-cerulean rounded-[20px] w-5 h-5">
                <Text tone="secondary">{items.length}</Text>
              </div>
            );
          }}
          suffix={({ isOpen, toggle }) => {
            return (
              <Button onPress={toggle} size="inline" variant="simple">
                <Icon
                  height={20}
                  name={isOpen ? "hand-pointer-down" : "hand-pointer"}
                  tone="none"
                  width={20}
                />
              </Button>
            );
          }}
          testId="offer-type-search-filter"
          uniqueItemId={(item) => {
            return item.id as string;
          }}
          variant="select"
          withReset={true}
        />
        <Combobox
          className="w-full"
          inputDisplayValue={(items) => {
            return items
              .map((item) => {
                return item.value;
              })
              .join(", ");
          }}
          itemComparator={(first, second) => {
            return first.id === second.id;
          }}
          items={orderStatus}
          label="stan zamówienia"
          listItemRenderer={({ item, isSelected }) => {
            return (
              <div>
                <Checkbox isSelected={isSelected} name="">
                  <strong>{item.value}</strong>
                </Checkbox>
              </div>
            );
          }}
          maxListHeight="auto"
          multiple={true}
          onSelect={(values) => {
            onOrderStatusChange(
              values.map((item) => {
                return item.id as string;
              }),
            );
          }}
          openOnFocus={true}
          prefix={({ items }) => {
            return (
              <div className="flex justify-center items-center bg-cerulean rounded-[20px] w-5 h-5">
                <Text tone="secondary">{items.length}</Text>
              </div>
            );
          }}
          suffix={({ isOpen, toggle }) => {
            return (
              <Button onPress={toggle} size="inline" variant="simple">
                <Icon
                  height={20}
                  name={isOpen ? "hand-pointer-down" : "hand-pointer"}
                  tone="none"
                  width={20}
                />
              </Button>
            );
          }}
          testId="order-status-search-filter"
          uniqueItemId={(item) => {
            return item.id as string;
          }}
          variant="select"
          withReset={true}
        />
      </div>
      <div className="flex gap-x-2 gap-y-3 only-mobile:flex-col">
        <Combobox
          className="w-full"
          inputDisplayValue={(items) => {
            return items
              .map((item) => {
                return item.value;
              })
              .join(", ");
          }}
          itemComparator={(first, second) => {
            return first.id === second.id;
          }}
          items={paymentStatus}
          label="status płatności"
          listItemRenderer={({ item, isSelected }) => {
            return (
              <div>
                <Checkbox isSelected={isSelected} name="">
                  <strong>{item.value}</strong>
                </Checkbox>
              </div>
            );
          }}
          maxListHeight="auto"
          multiple={true}
          onSelect={(values) => {
            onPaymentStatusChange(
              values.map((item) => {
                return item.id as string;
              }),
            );
          }}
          openOnFocus={true}
          prefix={({ items }) => {
            return (
              <div className="flex justify-center items-center bg-cerulean rounded-[20px] w-5 h-5">
                <Text tone="secondary">{items.length}</Text>
              </div>
            );
          }}
          suffix={({ isOpen, toggle }) => {
            return (
              <Button onPress={toggle} size="inline" variant="simple">
                <Icon
                  height={20}
                  name={isOpen ? "hand-pointer-down" : "hand-pointer"}
                  tone="none"
                  width={20}
                />
              </Button>
            );
          }}
          testId="payment-status-search-filter"
          uniqueItemId={(item) => {
            return item.id as string;
          }}
          variant="select"
          withReset={true}
        />
        <Combobox
          className="w-full"
          inputDisplayValue={(items) => {
            return items
              .map((item) => {
                return item.value;
              })
              .join(", ");
          }}
          itemComparator={(first, second) => {
            return first.id === second.id;
          }}
          items={cancellationReason}
          label="powód anulowania"
          listItemRenderer={({ item, isSelected }) => {
            return (
              <div>
                <Checkbox isSelected={isSelected} name="">
                  <strong>{item.value}</strong>
                </Checkbox>
              </div>
            );
          }}
          maxListHeight="auto"
          multiple={true}
          onSelect={(values) => {
            onCancellationReasonChange(
              values.map((item) => {
                return item.id as string;
              }),
            );
          }}
          openOnFocus={true}
          prefix={({ items }) => {
            return (
              <div className="flex justify-center items-center bg-cerulean rounded-[20px] w-5 h-5">
                <Text tone="secondary">{items.length}</Text>
              </div>
            );
          }}
          suffix={({ isOpen, toggle }) => {
            return (
              <Button onPress={toggle} size="inline" variant="simple">
                <Icon
                  height={20}
                  name={isOpen ? "hand-pointer-down" : "hand-pointer"}
                  tone="none"
                  width={20}
                />
              </Button>
            );
          }}
          testId="cancellation-reason-search-filter"
          uniqueItemId={(item) => {
            return item.id as string;
          }}
          variant="select"
          withReset={true}
        />
      </div>
    </div>
  );
}
